import { useState, useMemo } from "react";
import { ChevronDown, Search, X, Stethoscope } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "./ui/dropdown-menu";
import { cn } from "../lib/utils";

/**
 * Searchable single-select for choosing a doctor.
 *
 * Radix's Select has no filter box, so this is a dropdown with a search input
 * at the top that narrows the list in memory — the doctor list is small enough
 * to load once. Clearing is done from the trigger itself, so removing a
 * provider filter doesn't require opening the menu.
 */
export default function ProviderSelect({ doctors = [], value, onChange, className }) {
  const [query, setQuery] = useState("");
  const selected = doctors.find((d) => d._id === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return doctors;
    return doctors.filter(
      (d) =>
        d.name?.toLowerCase().includes(q) ||
        d.specialty?.toLowerCase().includes(q) ||
        d.department_id?.name?.toLowerCase().includes(q)
    );
  }, [doctors, query]);

  return (
    <DropdownMenu onOpenChange={(open) => !open && setQuery("")}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "h-9 w-full justify-between gap-2 bg-card px-3 font-normal",
            selected ? "border-primary/40 bg-primary/5" : "text-muted-foreground",
            className
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            <Stethoscope className="h-3.5 w-3.5 shrink-0 opacity-60" />
            <span className="truncate">{selected ? selected.name : "All providers"}</span>
          </span>
          <span className="flex shrink-0 items-center">
            {selected ? (
              <span
                role="button"
                tabIndex={-1}
                aria-label="Clear provider filter"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange("");
                }}
                className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            ) : (
              <ChevronDown className="h-3.5 w-3.5 opacity-50" />
            )}
          </span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-[16rem] p-0">
        <div className="border-b border-border p-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              placeholder="Search by name or specialty…"
              className="h-8 pl-8 text-sm"
            />
          </div>
        </div>

        <div className="max-h-72 overflow-y-auto p-1">
          <DropdownMenuItem
            onSelect={() => onChange("")}
            className={cn("py-2 text-muted-foreground", !value && "bg-accent text-foreground")}
          >
            All providers
          </DropdownMenuItem>

          {filtered.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">No doctors found.</p>
          ) : (
            filtered.map((doctor) => (
              <DropdownMenuItem
                key={doctor._id}
                onSelect={() => onChange(doctor._id)}
                className={cn("flex-col items-start gap-0 py-2", doctor._id === value && "bg-accent")}
              >
                <span className="font-medium">{doctor.name}</span>
                {(doctor.department_id?.name || doctor.specialty) && (
                  <span className="text-xs text-muted-foreground">
                    {[doctor.department_id?.name, doctor.specialty].filter(Boolean).join(" · ")}
                  </span>
                )}
              </DropdownMenuItem>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
